"use client";

import React from "react";
import { Send, CircleCheck, CircleAlert, Loader, Phone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button, buttonVariants } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="225576" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/20 flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-black italic uppercase tracking-tighter text-foreground mb-2">Request Received!</h3>
        <p className="text-muted-foreground max-w-md mb-8">
          {successMessage}
        </p>
        <Link to="tel:+14243138474" className={cn(buttonVariants({
        variant: "outline"
      }), "rounded-sm px-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all")}>
          Call Now
        </Link>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-input/50 h-12 rounded-sm border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs italic">Enter your name as it appears on your ID.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-input/50 h-12 rounded-sm border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs italic">We'll use this to send your quote.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(323) 555-0199" className="bg-input/50 h-12 rounded-sm border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="text-xs italic">For urgent tire service updates.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Service Needed</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-12 w-full appearance-none rounded-sm border border-input bg-input/50 px-3 py-1 text-base shadow-xs transition-[color,box-shadow] focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                    <option value="" disabled>Select an option</option>
                    <option value="Tire Replacement">Tire Replacement</option>
                    <option value="Flat Tire Repair">Flat Tire Repair</option>
                    <option value="Wheel Alignment">Wheel Alignment</option>
                    <option value="TPMS Sensor Service">TPMS Sensor Service</option>
                    <option value="Brake Service">Brake Service</option>
                    <option value="Oil Change">Oil Change</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-muted-foreground">
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>
              </FormControl>
              <FormDescription className="text-xs italic">Choose the service that fits your needs.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="description" rules={{
      required: "Details are required",
      minLength: {
        value: 10,
        message: "Please provide more details (min 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your tire sizes, quantities, or specific vehicle issues." className="min-h-[150px] bg-input/50 rounded-sm border-border focus-visible:ring-primary resize-y" {...field} />
            </FormControl>
            <FormDescription className="text-xs italic">Include tire sizes or fleet details for faster processing.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem className="md:w-1/2">
            <FormLabel className="text-foreground font-default font-bold uppercase tracking-tight">Estimated Quantity / Budget</FormLabel>
            <FormControl>
              <Input type="text" placeholder="e.g. 50 used tires" className="bg-input/50 h-12 rounded-sm border-border focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="text-xs italic">Help us understand the scale of your request.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-sm bg-destructive/10 border border-destructive/20 flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-end">
        <Link to="tel:+14243138474" className={cn(buttonVariants({
        variant: "default",
        size: "lg"
      }), "w-full md:w-auto rounded-sm px-10 text-primary-foreground font-default font-black italic uppercase tracking-tighter shadow-lg hover:shadow-primary/20 hover:-translate-y-1 transition-all")}>
          Call Now
          <Phone className="ml-2 size-4" />
        </Link>
      </div>
    </Form>;
}
export default function ReachOurLAShop() {
  return <section id="reach-our-la-shop" className="relative py-24 lg:py-32 bg-background text-foreground overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-50" />
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-border to-transparent opacity-20" />
        <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-border to-transparent opacity-20" />
        <div className="absolute -top-40 -right-40 size-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 size-96 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 text-left">
            <h2 className="text-sm font-default font-black tracking-widest text-primary uppercase mb-4 italic">
              Get Back On The Road
            </h2>
            <h3 className="text-5xl md:text-6xl font-default font-black text-foreground mb-8 tracking-tighter uppercase italic leading-none">
              Reach Our <br />LA Shop
            </h3>
            <p className="text-xl text-muted-foreground mb-10 font-default">
              Need immediate assistance? Our Los Angeles technicians are standing by for same-day tire repair and replacement.
            </p>
            
            <div className="space-y-6">
              <Link to="tel:+14243138474" className="flex items-center justify-center gap-4 w-full bg-primary py-6 rounded-sm text-primary-foreground font-default font-black italic uppercase text-2xl tracking-tighter shadow-xl hover:shadow-primary/40 hover:-translate-y-1 transition-all">
                Call Now
              </Link>
              <p className="text-center text-sm text-muted-foreground font-mono uppercase tracking-widest">
                Fast Same-Day Service Guaranteed
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-card border border-border/50 rounded-sm shadow-2xl shadow-black/20 p-6 md:p-10 backdrop-blur-md">
              <div className="mb-8">
                <h4 className="text-xl font-default font-black italic uppercase tracking-tight text-foreground">Non-Urgent Inquiries</h4>
                <p className="text-muted-foreground text-sm">For Tire Replacement, Flat Tire Repair, Wheel Alignment, TPMS Sensor Service, Brake Service, Oil Change, fill out the form below.</p>
              </div>
              <FormProvider formKey="reach_form_contact" formId="reach_form_contact" sectionName="ReachOurLAShop" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_type: "",
              description: "",
              budget: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              project_type: {
                required: true
              },
              description: {
                required: true,
                minLength: 10
              }
            }} submitText="Call Now" successMessage="Your inquiry has been received. A Nova Tire Center specialist will contact you within 24 hours with your custom quote." errorMessage="Transmission failed. Please check your connection or call our shop directly for immediate assistance.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}